<template>
	<modal id="add-user-modal"
		title="Add user"
        :subtitle="stepTitle"
		:active="active"
		:dismissable="true"
		:max-width="1170"
		@dismiss="dismiss"
	>
        <div slot="header-actions">
            <progress-bar
                :min="0"
                :max="2"
                :value="step"
                :progress="true"
                type="info"
                style="width: 100px;"
            >
                <div v-if="step <= steps.length" slot="progress" slot-scope="progressData">
                    Step {{progressData.value}} of {{progressData.max}}
                </div>
            </progress-bar>
        </div>

		<modal-body class="p-0">
            <div v-show="step === 1">
                <user-picker
                    source="unregistered"
                    :selectable="true"
                    @change="onUserChange"
                />
            </div>

            <div v-show="step === 2" class="px-1g5 pb-1">

                <form @submit.prevent="createUser">
                    <form-section label-width="100">

                        <control-group title="User">
                            <div v-control:static v-if="user">
                                <div>
                                    {{user.firstName}} {{user.lastName}}
                                    <tooltip :title="user.emailAddress" />
                                </div>
                                <div class="text-sm text-dark-secondary">{{user.workAreaName}}</div>
                            </div>
                            <div v-control:static v-else>
                                <span class="text-link" @click="previous">Choose a user</span>
                            </div>
                        </control-group>
                        <control-group title="Groups" :valid="!$v.selectedGroupRequests.$error">
                            <template v-if="$v.selectedGroupRequests.$error">
                                <control-error v-control:static v-if="!$v.selectedGroupRequests.required || !$v.selectedGroupRequests.minLength">
                                    Choose at least one group you want {{user.firstName}} added to
                                </control-error>
                            </template>

                            <user-group-selector
                                source="unregistered"
                                :selectable="true"
                                @change="onSelectedGroupsChange"
                            />
                        </control-group>
                    </form-section>
                </form>
            </div>
		</modal-body>

		<modal-actions>
			<btn link @click="cancel" class="btn-left">Cancel</btn>

            <btn v-if="step == steps.length" primary @click="createUser">Add user</btn>
            <btn v-if="step < steps.length" :disabled="!user" primary @click="next">Continue</btn>
            <btn v-if="step > 1" link @click="previous">Go back</btn>
		</modal-actions>
		
	</modal>
</template>


<script>
    import _ from 'lodash';
    import { required, minLength } from 'validators';
	import { notifications } from 'pdk/services';
    import { AsyncMixin, FormValidationMixin, ModalMixin } from 'pdk/mixins';
    import { users } from '@/services';
    import { UserPicker, UserGroupSelector } from '@/components/users';


	export default {
		name: 'add-registered-user-modal',
		components: {
            UserPicker,
            UserGroupSelector
        },
        mixins: [AsyncMixin, FormValidationMixin, ModalMixin],
        data() {
            return {
                user: undefined,
                selectedGroups: undefined,
                step: 1,
                steps: [
                    {
                        title: 'Choose a Support Center user'
                    },
                    {
                        title: 'Choose the user\'s groups'
                    }
                ]
            };
        },
        validations: {
            selectedGroupRequests: {
                required,
                minLength: minLength(1)
            }
        },
        computed: {
            stepTitle() {
                switch (this.step) {
                    case 1: return 'Step 1: Choose a Support Center user';
                    case 2: return `Step 2: Choose ${this.user.firstName}'s groups`;
                    default: return null;
                }
            },
            selectedGroupRequests() {
                return _.map(this.selectedGroups, (g) => {
                    return {
                        globalUserID: this.user.globalUserID,
                        locationGroupID: g.locationGroupID,
                        role: g.role
                    };
                });
            }
        },
        watch: {
            step() {
                this.resetValidation();
            }
        },
        methods: {
            onOpen() {
                this.step = 1;
                this.user = undefined;
            },
            onClose() {
                this.resetValidation();
                this.step = 1;
                this.user = undefined;
            },

            next() {
                this.step++;
            },
            previous() {
                this.step--;
            },

            createUser() {
                if (!this.validate()) return;

                this.async(users.createUser({
					globalUserID: this.user.globalUserID,
					userName: this.user.userName,
					firstName: this.user.firstName,
					lastName: this.user.lastName,
					emailAddress: this.user.emailAddress
                })).then(() => {

                    // Add the user to the requested groups
                    this.async(users.addUserToGroups({
                        globalUserID: this.user.globalUserID,
                        groups: this.selectedGroupRequests
                    })).then(() => {
                        this.$emit('add', this.user, this.selectedGroupRequests);
                        notifications.open({
                            content: `User added`
                        });
                        this.close();
                    });
                });
            },
            onUserChange(user) {
                this.user = user;
            },
            onSelectedGroupsChange(groups) {
                this.selectedGroups = groups;
            }
		}
	};
</script>


<style lang="scss">
@import '~@/assets/styles/references';

  #add-users-grid .k-command-cell {
	padding: 4px 8px;
	text-align: center;
  }

  #add-users-grid .k-button {
    margin: calc( (1.42857em + (-1 * calc( 4px + 1.42857em)))/2) 0 calc( (1.42857em + (-1 * calc( 3px + 1.42857em)))/2) 0;
	min-width: 0;
  }

  #add-users-grid .k-icon {
	  margin: 0 auto;
  }

</style>
